// src/chat-manager.ts
import type { ChatMessage } from './protocol';

export interface SendReceipt {
  attempted: number;
  sent: number;
}

export interface DeliveryState {
  attempted: number;
  sent: number;
  acked: ReadonlySet<string>;
}

/** Copy under your own bubble. `acked` counts peers, not opens. */
export function deliveryLabel(attempted: number, sent: number, acked: number): string {
  if (attempted === 0 || sent === 0) return 'Not sent · no one else is connected';
  if (acked <= 0) return 'Sent';
  const got = Math.min(acked, attempted);
  return `Delivered to ${got} of ${attempted}`;
}

/** Second ack from the same peer does not raise the count. */
export function applyAck(state: DeliveryState, peerId: string): DeliveryState {
  if (state.acked.has(peerId)) return state;
  const acked = new Set(state.acked);
  acked.add(peerId);
  return { ...state, acked };
}

function newMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export class ChatManager {
  private container: HTMLElement;
  private messageList: HTMLElement;
  private input: HTMLInputElement;
  private sendBtn: HTMLButtonElement;
  private myPeerId: string = '';
  private myName: string = '';
  private myColor: string = '';
  private outgoing = new Map<string, DeliveryState>();

  constructor(
    containerId: string,
    private readonly onSend: (text: string, name: string | undefined, id: string) => SendReceipt,
    private readonly onUnread?: (msg: ChatMessage) => void,
    /** Resolves a peer id to their car color; values come from the app's
        own validated palette, never raw peer input. */
    private readonly colorForPeer?: (peerId: string) => string,
  ) {
    this.container = document.getElementById(containerId) as HTMLElement;

    this.container.innerHTML = `
      <div class="flex flex-col min-h-0 max-h-[min(50vh,32rem)]">
        <div class="flex items-center justify-between px-4 py-3 border-b border-white/[0.08]">
          <span class="text-[11px] uppercase tracking-[0.2em] text-tesla-muted font-semibold">Chat</span>
          <button id="chat-close" class="text-tesla-muted text-2xl leading-none px-2" aria-label="Close chat">×</button>
        </div>
        <div id="chat-messages" class="overflow-y-auto p-3 space-y-3 flex-1 min-h-[10rem]">
          <p id="chat-empty" class="text-tesla-muted text-sm leading-snug">
            Messages stay on the devices in this room. Nothing is stored on a server.
          </p>
        </div>
        <div class="flex gap-2 p-3 border-t border-white/[0.08]">
          <input id="chat-input" type="text" placeholder="Type a message…"
            class="flex-1 bg-black/60 border border-white/15 rounded-xl px-3 py-3 text-white text-base placeholder-tesla-muted focus:outline-none focus:border-tesla-cyan min-h-[48px]"
            autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"/>
          <button id="chat-send"
            class="bg-tesla-cyan text-white font-semibold px-5 rounded-full min-h-[48px] min-w-[72px] text-base active:opacity-75">
            Send
          </button>
        </div>
      </div>
    `;

    this.messageList = this.container.querySelector('#chat-messages')!;
    this.input = this.container.querySelector('#chat-input')!;
    this.sendBtn = this.container.querySelector('#chat-send')!;

    this.sendBtn.addEventListener('click', () => this.doSend());
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.doSend();
    });

    const closeBtn = this.container.querySelector('#chat-close')!;
    closeBtn.addEventListener('click', () => {
      this.container.classList.add('hidden');
    });
  }

  setMyPeerId(id: string): void {
    this.myPeerId = id;
  }

  setMyName(name: string): void {
    this.myName = name;
  }

  setMyColor(color: string): void {
    this.myColor = color;
  }

  receive(msg: ChatMessage, notify = true): void {
    const label = msg.name?.trim() || msg.peerId.slice(0, 8);
    this.appendMessage(msg.text, 'peer', label, this.colorForPeer?.(msg.peerId));
    if (notify && this.container.classList.contains('hidden')) {
      this.onUnread?.(msg);
    }
  }

  /** Count a peer once. Unknown ids (not sent from this panel) are ignored. */
  noteAck(messageId: string, fromPeerId: string): void {
    const current = this.outgoing.get(messageId);
    if (!current || !fromPeerId) return;
    const next = applyAck(current, fromPeerId);
    this.outgoing.set(messageId, next);
    this.setDeliveryText(messageId, deliveryLabel(next.attempted, next.sent, next.acked.size));
  }

  private doSend(): void {
    const text = this.input.value.trim();
    if (!text) return;
    this.input.value = '';
    const id = newMessageId();
    const receipt = this.onSend(text, this.myName || undefined, id);
    const state: DeliveryState = { attempted: receipt.attempted, sent: receipt.sent, acked: new Set() };
    this.outgoing.set(id, state);
    this.appendMessage(
      text,
      'self',
      this.myName || 'You',
      this.myColor || undefined,
      id,
      deliveryLabel(state.attempted, state.sent, 0),
    );
  }

  private setDeliveryText(messageId: string, label: string): void {
    let el: Element | null = null;
    for (const node of this.messageList.querySelectorAll('[data-delivery]')) {
      if (node.getAttribute('data-delivery') === messageId) {
        el = node;
        break;
      }
    }
    if (!el) return;
    el.textContent = label;
    el.classList.toggle('text-yellow-200', label.startsWith('Not sent'));
    el.classList.toggle('text-tesla-muted', !label.startsWith('Not sent'));
  }

  private appendMessage(
    text: string,
    role: 'self' | 'peer',
    label: string,
    color?: string,
    messageId?: string,
    delivery?: string,
  ): void {
    this.messageList.querySelector('#chat-empty')?.remove();
    const div = document.createElement('div');
    div.className = role === 'self' ? 'text-right' : 'text-left';
    const status = role === 'self' && messageId && delivery
      ? `<div class="text-[11px] mt-1 ${delivery.startsWith('Not sent') ? 'text-yellow-200' : 'text-tesla-muted'}" data-delivery="${escapeHtml(messageId)}">${escapeHtml(delivery)}</div>`
      : '';
    div.innerHTML = `
      <span class="text-xs ${color ? 'font-semibold' : 'text-tesla-muted'}"${color ? ` style="color:${color}"` : ''}>${escapeHtml(label)}</span>
      <div class="inline-block rounded-2xl px-3.5 py-2 mt-1 text-sm max-w-[85%] break-words text-left
        ${role === 'self' ? 'bg-tesla-cyan text-white rounded-br-md' : 'bg-white/[0.07] border border-white/10 text-white rounded-bl-md'}">
        ${escapeHtml(text)}
      </div>
      ${status}`;
    this.messageList.appendChild(div);
    this.messageList.scrollTop = this.messageList.scrollHeight;
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
